// Turn engine — the headless heart of the chat orchestration. Owns the
// per-thread turn lifecycle: send, consume the SSE stream through one
// shared reducer, buffer streaming bubbles, commit on settle, queue a
// send fired mid-turn, cancel, and cold-reattach to an in-flight turn.
//
// View-agnostic by construction: it imports `api` (wire client) and
// `session` (pure helpers), nothing visual. Desktop and mobile both
// drive it; the only thing that ever differs is who renders the maps.
//
// Written fresh, not copied: it takes App.tsx's more complete logic
// (cancel / queue-next / compacted-snapshot reattach — all three missing
// from the old MobileShell) and MobileShell's streaming-buffer model
// (history stays immutable per token so memoised rows skip re-render),
// and preserves the React 19 StrictMode fix MobileShell documented:
// compute from a ref, dispatch already-computed values, keep updaters
// pure (see `useRefState` / `commitStreaming`).

import { useCallback, useRef, useState } from 'react'
import { api, isMyTurn } from '../api'
import { bubblesFromCompactedSnapshot, flattenSession } from './session'
import type { ChatMessage, QueuedSend, ThreadUsage, TurnEvent } from './types'

/**
 * `useState` plus a ref kept in lock-step. Async code (the stream reducer,
 * the send `finally`) reads `ref.current` for an always-current value
 * without closing over a stale render snapshot; the updater form also
 * reads the ref, so two dispatches in one tick compose correctly. A
 * referentially-equal result short-circuits the render.
 */
function useRefState<T>(initial: T) {
  const [value, setValue] = useState<T>(initial)
  const ref = useRef<T>(value)
  const set = useCallback((next: T | ((prev: T) => T)) => {
    const computed =
      typeof next === 'function' ? (next as (prev: T) => T)(ref.current) : next
    if (computed === ref.current) return
    ref.current = computed
    setValue(computed)
  }, [])
  return [value, ref, set] as const
}

export interface TurnEngineCallbacks {
  /** Fired once a turn settles (success, agent error, or cancel). The card
   *  store refetches the project; the session registry does unread
   *  bookkeeping. Decoupled via callback so the engine never imports a
   *  store (see doc/10 §4.2 + the architecture map's risk #6). */
  onTurnFinished?: (threadId: string, projectId: string) => void
  /** A real failure surfaced to the user as a toast. A user-initiated
   *  cancel is de-noised here — it never reaches this callback. */
  onError?: (message: string) => void
}

export interface TurnEngine {
  /** Committed history per thread. Immutable for a turn's duration. */
  messagesByThread: Record<string, ChatMessage[]>
  /** In-flight bubbles for the currently-streaming turn, per thread.
   *  Merged after `messagesByThread` at render time; promoted into it on
   *  settle. Kept separate so a per-token delta never reconciles history. */
  streamingByThread: Record<string, ChatMessage[]>
  busyByThread: Record<string, boolean>
  activeTurnByThread: Record<string, string>
  usageByThread: Record<string, ThreadUsage>
  queuedSendByThread: Record<string, QueuedSend>
  /** Start a turn. `displayText` is the user bubble (empty for a synthetic
   *  gap opener); `wireText` is what the agent receives (may be a wrapped
   *  discussing-gap envelope). Queues itself if the thread is already busy. */
  send: (
    threadId: string,
    projectId: string,
    displayText: string,
    wireText: string,
    imageRefs?: string[],
  ) => Promise<void>
  /** Trip the cancel token on this thread's in-flight turn. */
  cancel: (threadId: string) => Promise<void>
  /** Drop a thread's queued send — the user un-queued it before it drained. */
  cancelQueued: (threadId: string) => void
  /** Cold-attach: on thread open, detect an in-flight turn and follow its
   *  stream. Returns a cleanup to call when the thread closes. */
  reattach: (threadId: string, projectId: string) => () => void
  /** Lazy-load persisted history on first visit to a thread. No-op if the
   *  thread's slot already exists (cache hit or stream already filling). */
  loadHistory: (threadId: string) => Promise<void>
  /** Mark a brand-new thread as already-loaded (empty) so `loadHistory`
   *  skips the pointless fetch. */
  seedThread: (threadId: string) => void
  /** Mark a thread busy from a `thread_turn_started` SSE event (a turn
   *  started — possibly on another device). */
  noteTurnStarted: (threadId: string, turnId: string) => void
  /** Settle a thread from a `thread_turn_ended` SSE event. For a turn
   *  another device ran on a loaded thread, refetches the canonical
   *  session so observer devices converge on the same messages. */
  noteTurnEnded: (threadId: string, turnId: string) => void
}

export function useTurnEngine(callbacks: TurnEngineCallbacks = {}): TurnEngine {
  // Callbacks via ref so the non-memoised `send` always calls the latest.
  const cbRef = useRef(callbacks)
  cbRef.current = callbacks

  const [messagesByThread, messagesRef, setMessages] = useRefState<
    Record<string, ChatMessage[]>
  >({})
  const [streamingByThread, streamingRef, setStreaming] = useRefState<
    Record<string, ChatMessage[]>
  >({})
  const [busyByThread, busyRef, setBusy] = useRefState<Record<string, boolean>>(
    {},
  )
  const [activeTurnByThread, activeRef, setActive] = useRefState<
    Record<string, string>
  >({})
  const [queuedSendByThread, queuedRef, setQueued] = useRefState<
    Record<string, QueuedSend>
  >({})
  const [usageByThread, setUsageByThread] = useState<
    Record<string, ThreadUsage>
  >({})

  // Threads whose live SSE stream THIS hook instance owns (a local `send`
  // is feeding events in). `reattach` checks this to avoid double-attaching
  // — re-subscribing to a stream we already own would duplicate every
  // bubble.
  const localStreamsRef = useRef<Set<string>>(new Set())

  // Per-thread array updaters that skip the dispatch when the inner array
  // is unchanged (a no-op event like `thinking_end` with no open bubble).
  const updateMessages = useCallback(
    (tid: string, fn: (prev: ChatMessage[]) => ChatMessage[]) => {
      setMessages((prev) => {
        const cur = prev[tid] ?? []
        const next = fn(cur)
        return next === cur ? prev : { ...prev, [tid]: next }
      })
    },
    [setMessages],
  )
  const updateStreaming = useCallback(
    (tid: string, fn: (prev: ChatMessage[]) => ChatMessage[]) => {
      setStreaming((prev) => {
        const cur = prev[tid] ?? []
        const next = fn(cur)
        return next === cur ? prev : { ...prev, [tid]: next }
      })
    },
    [setStreaming],
  )

  /**
   * The one SSE reducer. Both a local `send` and a cold `reattach` feed
   * their stream through this, so bubble assembly is identical and stays
   * in sync if the protocol grows event types. Writes go to the streaming
   * buffer; `turn_usage` is a side write; `error` is de-noised.
   *
   * `toolNames` is per-stream — it resolves a `tool_use_end` whose
   * `tool_use_start` aged out of the replay buffer (folded into a
   * `compacted_snapshot`). `turnId` is known only on reattach.
   */
  const handleStreamEvent = useCallback(
    (
      tid: string,
      evt: TurnEvent,
      toolNames: Map<string, string>,
      turnId?: string,
    ) => {
      switch (evt.type) {
        case 'assistant_start':
          // No-op: an empty bubble here strands a blank rectangle before
          // thinking_delta / text_delta open their own. Let them be lazy.
          break
        case 'text_delta':
          updateStreaming(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (last && last.kind === 'assistant' && last.streaming) {
              const u = prev.slice()
              u[u.length - 1] = { ...last, text: last.text + evt.text }
              return u
            }
            return [
              ...prev,
              { kind: 'assistant', text: evt.text, ts: Date.now(), streaming: true },
            ]
          })
          break
        case 'assistant_end':
          updateStreaming(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (!last || last.kind !== 'assistant' || !last.streaming) return prev
            const u = prev.slice()
            u[u.length - 1] = {
              ...last,
              streaming: false,
              truncated: evt.truncated === true ? true : last.truncated,
            }
            return u
          })
          break
        case 'thinking_delta':
          updateStreaming(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (last && last.kind === 'thinking' && !last.done) {
              const u = prev.slice()
              u[u.length - 1] = { ...last, text: last.text + evt.text }
              return u
            }
            return [
              ...prev,
              { kind: 'thinking', text: evt.text, done: false, ts: Date.now() },
            ]
          })
          break
        case 'thinking_end':
          updateStreaming(tid, (prev) => {
            const last = prev[prev.length - 1]
            if (!last || last.kind !== 'thinking' || last.done) return prev
            const u = prev.slice()
            u[u.length - 1] = { ...last, done: true }
            return u
          })
          break
        case 'tool_use_start':
          toolNames.set(evt.id, evt.name)
          updateStreaming(tid, (prev) => [
            ...prev,
            {
              kind: 'tool_call',
              tool: evt.name,
              input: '',
              ts: Date.now(),
              toolUseId: evt.id,
            },
          ])
          break
        case 'tool_use_input_delta':
          updateStreaming(tid, (prev) => {
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (m.kind === 'tool_call' && m.toolUseId === evt.id) {
                const u = prev.slice()
                u[i] = { ...m, input: m.input + evt.partial }
                return u
              }
            }
            return prev
          })
          break
        case 'tool_use_end':
          updateStreaming(tid, (prev) => {
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (m.kind === 'tool_call' && m.toolUseId === evt.id) {
                const u = prev.slice()
                u[i] = { ...m, input: evt.input }
                return u
              }
            }
            // No bubble to merge into — the `tool_use_start` aged out and
            // was folded into a compacted_snapshot. Push a complete one.
            return [
              ...prev,
              {
                kind: 'tool_call',
                tool: toolNames.get(evt.id) ?? 'tool',
                input: evt.input,
                ts: Date.now(),
                toolUseId: evt.id,
              },
            ]
          })
          break
        case 'compacted_snapshot': {
          const rebuilt = bubblesFromCompactedSnapshot(evt, toolNames, turnId)
          if (rebuilt.length > 0) {
            updateStreaming(tid, (prev) => [...prev, ...rebuilt])
          }
          break
        }
        case 'turn_usage':
          setUsageByThread((prev) => ({
            ...prev,
            [tid]: {
              session_total_input: evt.session_total_input,
              session_total_output: evt.session_total_output,
              session_total_cache_creation: evt.session_total_cache_creation,
              session_total_cache_read: evt.session_total_cache_read,
              pre_compact_messages: evt.pre_compact_messages,
              post_compact_messages: evt.post_compact_messages,
              system_tokens: evt.system_tokens,
              tools_tokens: evt.tools_tokens,
              compact_triggered: evt.compact_triggered,
              compacted_message_count: evt.compacted_message_count,
              context_window: evt.context_window,
              compact_threshold: evt.compact_threshold,
              estimated_next_input: evt.estimated_next_input,
            },
          }))
          break
        case 'error':
          // A user cancel surfaces as this exact message — swallow it; the
          // streamed-so-far reply stays in the buffer and `commitStreaming`
          // freezes it. Anything else is a real failure.
          if (evt.message !== 'stopped by user') {
            cbRef.current.onError?.(`agent: ${evt.message}`)
          }
          break
        // tool_result / turn_done carry no bubble.
      }
    },
    [updateStreaming],
  )

  /**
   * Promote the streaming buffer into permanent history at turn end
   * (success OR failure). Any bubble still flagged streaming / !done is
   * finalised so a drop mid-turn leaves a frozen partial reply.
   *
   * `finalized` is computed synchronously from `streamingRef` BEFORE any
   * dispatch — the React 19 StrictMode fix MobileShell documented: a
   * closure variable shared between two updaters got overwritten when the
   * first updater was replayed with the just-computed state.
   */
  const commitStreaming = useCallback(
    (tid: string) => {
      const segment = streamingRef.current[tid] ?? []
      const finalized: ChatMessage[] = segment.map((m) => {
        if (m.kind === 'assistant' && m.streaming) return { ...m, streaming: false }
        if (m.kind === 'thinking' && !m.done) return { ...m, done: true }
        return m
      })
      if (tid in streamingRef.current) {
        setStreaming((prev) => {
          if (!(tid in prev)) return prev
          const rest = { ...prev }
          delete rest[tid]
          return rest
        })
      }
      if (finalized.length > 0) {
        updateMessages(tid, (prev) => [...prev, ...finalized])
      }
    },
    [streamingRef, setStreaming, updateMessages],
  )

  /** Common turn-settle path: commit the buffer, clear busy + the turn id,
   *  release stream ownership, notify subscribers. */
  const settleTurn = useCallback(
    (threadId: string, projectId: string) => {
      commitStreaming(threadId)
      setBusy((prev) => (prev[threadId] ? { ...prev, [threadId]: false } : prev))
      setActive((prev) => {
        if (!(threadId in prev)) return prev
        const rest = { ...prev }
        delete rest[threadId]
        return rest
      })
      localStreamsRef.current.delete(threadId)
      cbRef.current.onTurnFinished?.(threadId, projectId)
    },
    [commitStreaming, setBusy, setActive],
  )

  // `send` is intentionally NOT memoised: it recurses (the queue drain) and
  // a useCallback can't list itself in its own deps without a TDZ error.
  // Recreated each render, it still behaves correctly across renders
  // because every piece of mutable state it touches is read via a ref.
  const send = async (
    threadId: string,
    projectId: string,
    displayText: string,
    wireText: string,
    imageRefs: string[] = [],
  ): Promise<void> => {
    // Queue-on-busy: a turn is already streaming on this thread. Snapshot
    // and bail — the in-flight turn's `finally` drains the slot on settle.
    if (busyRef.current[threadId]) {
      setQueued((prev) => ({
        ...prev,
        [threadId]: { displayText, wireText, imageRefs, projectId },
      }))
      return
    }
    // Optimistic user bubble. A synthetic gap opener passes an empty
    // displayText and skips it — there is no user input to show.
    if (displayText) {
      updateMessages(threadId, (prev) => [
        ...prev,
        { kind: 'user', text: displayText, ts: Date.now() },
      ])
    }
    setBusy((prev) => ({ ...prev, [threadId]: true }))
    localStreamsRef.current.add(threadId)

    const toolNames = new Map<string, string>()
    try {
      await api.streamMessage(
        threadId,
        wireText,
        (evt) => handleStreamEvent(threadId, evt, toolNames),
        imageRefs,
        (turnId) => {
          setActive((prev) =>
            prev[threadId] === turnId ? prev : { ...prev, [threadId]: turnId },
          )
        },
      )
    } catch (e) {
      cbRef.current.onError?.(`send failed: ${(e as Error).message}`)
    } finally {
      settleTurn(threadId, projectId)
      // Drain a queued send — pinned to its own origin thread + project so
      // it lands where the user queued it, not wherever they navigated to.
      const queued = queuedRef.current[threadId]
      if (queued) {
        setQueued((prev) => {
          const rest = { ...prev }
          delete rest[threadId]
          return rest
        })
        void send(
          threadId,
          queued.projectId,
          queued.displayText,
          queued.wireText,
          queued.imageRefs,
        )
      }
    }
  }

  const cancel = useCallback(
    async (threadId: string) => {
      const turnId = activeRef.current[threadId]
      if (!turnId) return
      try {
        await api.cancelTurn(threadId, turnId)
      } catch (e) {
        cbRef.current.onError?.(`cancel failed: ${(e as Error).message}`)
      }
    },
    [activeRef],
  )

  const cancelQueued = useCallback(
    (threadId: string) => {
      setQueued((prev) => {
        if (!(threadId in prev)) return prev
        const rest = { ...prev }
        delete rest[threadId]
        return rest
      })
    },
    [setQueued],
  )

  const reattach = useCallback(
    (threadId: string, projectId: string): (() => void) => {
      let cancelled = false
      let unsub: (() => void) | null = null
      void api
        .getActiveTurn(threadId)
        .then((active) => {
          if (cancelled || !active) return
          setBusy((prev) =>
            prev[threadId] ? prev : { ...prev, [threadId]: true },
          )
          setActive((prev) =>
            prev[threadId] === active.turn_id
              ? prev
              : { ...prev, [threadId]: active.turn_id },
          )
          // Already own this stream (a local send is feeding it) — a second
          // subscription would double every bubble.
          if (localStreamsRef.current.has(threadId)) return
          const toolNames = new Map<string, string>()
          unsub = api.attachTurn(
            threadId,
            active.turn_id,
            (evt) =>
              handleStreamEvent(threadId, evt, toolNames, active.turn_id),
            {
              onDone: () => settleTurn(threadId, projectId),
              onError: (e) => {
                cbRef.current.onError?.(
                  `stream lost: ${e instanceof Error ? e.message : String(e)}`,
                )
                settleTurn(threadId, projectId)
              },
            },
          )
        })
        .catch(() => {
          /* getActiveTurn failed — non-fatal; a later send 409 surfaces it */
        })
      return () => {
        cancelled = true
        unsub?.()
      }
    },
    [handleStreamEvent, settleTurn, setBusy, setActive],
  )

  const loadHistory = useCallback(
    async (threadId: string): Promise<void> => {
      // Cache hit / a stream already started filling the slot — never
      // refetch, it would clobber accumulated deltas with a snapshot that
      // may not yet include the just-saved turn.
      if (threadId in messagesRef.current) return
      try {
        const [sessionMsgs, usageOrErr] = await Promise.all([
          api.listMessages(threadId),
          api.getThreadUsage(threadId).catch((e) => e as Error),
        ])
        if (!(usageOrErr instanceof Error)) {
          const usage = usageOrErr
          setUsageByThread((prev) =>
            threadId in prev ? prev : { ...prev, [threadId]: usage },
          )
        }
        const flat = flattenSession(sessionMsgs)
        setMessages((prev) =>
          threadId in prev ? prev : { ...prev, [threadId]: flat },
        )
      } catch (e) {
        cbRef.current.onError?.(`load thread failed: ${(e as Error).message}`)
      }
    },
    [messagesRef, setMessages],
  )

  const seedThread = useCallback(
    (threadId: string) => {
      setMessages((prev) =>
        threadId in prev ? prev : { ...prev, [threadId]: [] },
      )
    },
    [setMessages],
  )

  // External SSE-driven turn signals. CoreProvider routes the project SSE
  // here so the busy map reflects turns this hook instance never started.
  const noteTurnStarted = useCallback(
    (threadId: string, turnId: string) => {
      setBusy((prev) =>
        prev[threadId] ? prev : { ...prev, [threadId]: true },
      )
      setActive((prev) =>
        prev[threadId] === turnId ? prev : { ...prev, [threadId]: turnId },
      )
    },
    [setBusy, setActive],
  )

  const noteTurnEnded = useCallback(
    async (threadId: string, turnId: string) => {
      // A turn we started ourselves keeps its rich local view (incl. the
      // thinking deltas the server never persists) — never refetch it.
      // Another device's turn, on a thread we've loaded: pull the
      // canonical session so observer devices converge.
      if (!isMyTurn(turnId) && threadId in messagesRef.current) {
        try {
          const sessionMsgs = await api.listMessages(threadId)
          setMessages((prev) => ({
            ...prev,
            [threadId]: flattenSession(sessionMsgs),
          }))
        } catch {
          /* non-fatal — the next thread open refetches anyway */
        }
        try {
          const usage = await api.getThreadUsage(threadId)
          setUsageByThread((prev) => ({ ...prev, [threadId]: usage }))
        } catch {
          /* non-fatal — the context bar keeps its prior values */
        }
      }
      setBusy((prev) =>
        prev[threadId] ? { ...prev, [threadId]: false } : prev,
      )
      setActive((prev) => {
        if (!(threadId in prev)) return prev
        const rest = { ...prev }
        delete rest[threadId]
        return rest
      })
    },
    [messagesRef, setMessages, setBusy, setActive],
  )

  return {
    messagesByThread,
    streamingByThread,
    busyByThread,
    activeTurnByThread,
    usageByThread,
    queuedSendByThread,
    send,
    cancel,
    cancelQueued,
    reattach,
    loadHistory,
    seedThread,
    noteTurnStarted,
    noteTurnEnded,
  }
}
